/**
 * Public checkout-session types, aligned with `PublicCheckoutSession` in
 * api/openapi.yaml (GET /v1/checkout-sessions/{id}/public and the SSE stream).
 *
 * Two layers:
 *  - `Wire*`  : exactly what the API sends (canonical names + SDK aliases, chain_id as string).
 *  - the rest : what the page consumes after `normalizeSession()` in ./client.ts
 *               (chain_id coerced to number, aliases filled both ways, purpose normalised).
 */
export type Chain =
  | 'bitcoin'
  | 'ethereum'
  | 'base'
  | 'arbitrum'
  | 'optimism'
  | 'polygon'
  | 'bnb'
  | 'avalanche'
  | 'robinhood'
  | 'solana'
  | 'tron';

/** `<asset>:<chain>` canonical asset id, e.g. "usdc:base", "btc:bitcoin". */
export type AssetId = string;

export type SessionStatus = 'pending' | 'confirming' | 'paid' | 'underpaid' | 'overpaid' | 'expired' | 'cancelled';
export type PaymentStatus = 'detected' | 'confirming' | 'confirmed' | 'failed';
export type Purpose = 'payment' | 'credits';

export interface PaymentOption {
  asset: AssetId; // canonical id, use for logic / lookups
  symbol: string; // ticker for display (BTC, USDC, ...)
  chain: Chain;
  /** Canonical EVM chain id as decimal string (null for non-EVM). */
  network_id: string | null;
  /** Alias of network_id, coerced to a number by normalizeSession (undefined for non-EVM). */
  chain_id?: number;
  wallet_id?: string;
  address: string; // merchant-owned receiving address
  derivation_path?: string | null;
  /** Token contract / SPL mint (canonical). */
  contract_address: string | null;
  /** Alias of contract_address (undefined when native). */
  token_contract?: string;
  decimals: number;
  amount: string; // exact amount in asset units, decimal string
  amount_minor: string; // base units as integer string
  rate: string; // fiat per 1 asset at lock time
  rate_locked_until: string;
  payment_uri: string; // BIP-21 / EIP-681 / Solana Pay / tron:
  /** Alias of payment_uri. */
  uri: string;
  /** What to encode in the QR (bare address for Tron). */
  qr_payload: string;
  memo?: string | null;
  required_confirmations?: number;
  explorer_url?: string | null;
}

export interface PaymentRecord {
  id: string;
  chain: Chain;
  asset: AssetId;
  symbol: string;
  tx_hash: string;
  amount: string;
  amount_minor: string;
  status: PaymentStatus;
  confirmations: number;
  required_confirmations: number;
  explorer_url?: string | null;
  detected_at: string;
  confirmed_at?: string | null;
}

/** Aggregated received amounts; present once any payment is detected. */
export interface PaidSummary {
  asset: AssetId | null;
  amount_received: string;
  amount_received_fiat?: string;
  amount_due: string;
  amount_remaining: string;
  confirmations: number;
  required_confirmations: number;
  payment_ids: string[];
}

export interface AddressManifest {
  alg: 'ed25519';
  pubkey_id: string;
  /** Hex ed25519 signature over canonical sorted JSON of {merchant_id, issued_at, addresses}. */
  signature: string;
  merchant_id: string;
  issued_at: string;
  /** `<asset>:<chain>` -> address, exactly as signed. */
  addresses: Record<string, string>;
  /** Informational, not signed. */
  wallet_ids?: Record<string, string>;
}

/**
 * `DisplayRates` from the spec: 1 unit of the session's fiat `base` expressed in other fiats
 * (decimal strings, the base itself omitted). Indicative only; absent for crypto-priced
 * invoices and on older API versions.
 */
export interface DisplayRates {
  object?: 'display_rates';
  base: string;
  rates: Record<string, string>;
  as_of: string;
  source?: string;
}

/** Merchant defaults for the payer UI (`PublicCheckoutSession.locale_hints`). */
export interface LocaleHints {
  merchant_locale?: string | null;
  suggested_currency?: string | null;
}

export interface PublicSession {
  id: string; // cs_... or cpu_...
  object?: 'checkout_session.public';
  /** false = test-mode session (testnets), whatever network this build defaults to. */
  livemode?: boolean;
  purpose: Purpose;
  merchant: { name: string; logo_url?: string | null };
  amount: string;
  currency: string;
  description?: string | null;
  status: SessionStatus;
  expires_at: string;
  payment_options: PaymentOption[];
  paid: PaidSummary | null;
  payments: PaymentRecord[];
  success_url: string | null;
  cancel_url: string | null;
  /** When to redirect to success_url. Defaults to "paid". */
  redirect_on?: 'confirming' | 'paid';
  address_manifest?: AddressManifest | null;
  /** Shortfall (negative) / excess (positive) in `currency` when under/overpaid. */
  balance_delta?: string | null;
  branding?: { theme?: 'light' | 'dark' | 'auto'; primary_color?: string | null };
  locale?: string | null;
  display_rates?: DisplayRates | null;
  locale_hints?: LocaleHints | null;
  updated_at?: string;
}

/* ---------------------------------------------------------------- wire shape */

export type WirePaymentOption = Omit<PaymentOption, 'chain_id' | 'token_contract' | 'uri' | 'symbol' | 'network_id' | 'contract_address' | 'payment_uri' | 'qr_payload'> & {
  symbol?: string;
  network_id?: string | null;
  chain_id?: string | number | null;
  contract_address?: string | null;
  token_contract?: string | null;
  payment_uri?: string;
  uri?: string;
  qr_payload?: string;
};

export type WireSession = Omit<PublicSession, 'payment_options' | 'purpose' | 'paid' | 'payments' | 'success_url' | 'cancel_url'> & {
  purpose?: Purpose | 'purchase';
  payment_options: WirePaymentOption[];
  paid?: PaidSummary | null;
  payments?: Array<Partial<PaymentRecord> & Pick<PaymentRecord, 'id' | 'chain' | 'tx_hash' | 'status' | 'detected_at'>>;
  success_url?: string | null;
  cancel_url?: string | null;
};

export type SessionEvent = { type: 'status'; session: PublicSession } | { type: 'heartbeat'; ts: string };

export const TERMINAL_STATUSES: ReadonlySet<SessionStatus> = new Set(['paid', 'underpaid', 'overpaid', 'expired', 'cancelled']);
